/**
 * ScheduleMissingNotice — 「该校区还没配课表」的唯一出口 (v0.17.61)
 *
 * 【为什么要有这个文件】
 *   辉哥拍板部署路线 A: 上线后由管理员自己去「校区」tab 配课表 ⇒ **「没配课表」是会被真实用户
 *   遇到的常态状态, 不是异常态**。而在这之前它有两处死路:
 *
 *     ① 课表日历 (CalendarTab.js): 本轮之前根本不提示, 直接退回老的「真实时间刻度」渲染
 *        (一天 28 行) —— 用户看到的是一张**看起来正常、但跟真实排课规则毫无关系**的表。
 *     ② 约课弹窗 (LessonEditorModal.js) 的两处时间段占位: 一句「该校区未配置课表」, 说完就没了,
 *        既不知道去哪儿配, 也没法继续建课 —— 用户从「想约课」这条路进来会被**直接堵死**。
 *
 *   这两处必须给出**同一个**指引, 否则用户从约课那条路进来会遇到跟日历那边不一样的说法。
 *   本文件把**文案 + 出口**收到一处:
 *     · SCHEDULE_MISSING_TITLE / _BODY / _ACTION —— 三个字符串, 全仓库**只此一份**。
 *       (改前 LessonEditorModal.js 里那句 `const label = this.scheduleNodes ? ... : ...`
 *        本身就是复制了两遍: timeSlotDropdown 和 trialTimeSlotDropdown 各写一次,
 *        改一处忘了另一处必然分叉。)
 *     · goConfigureSchedule() —— 唯一的跳转出口, 靠一个 CustomEvent 跟页面通信。
 *     · scheduleMissingPanel() / scheduleMissingCompact() —— 两种版式, **文案同源**。
 *
 * 【为什么出口走 CustomEvent 而不是直接把 SchedulerIndexPage 的实例传下去】
 *   约课弹窗是 `app.modal.show(...)` 起的**全局浮层**, 它拿不到页面组件实例; 而日历那块是
 *   页面内的子组件, 能直接拿到。两条路要共用一个出口, 就只能挑两边都能用的那一种:
 *   页面组件 (SchedulerIndexPage) 监听一个 window 事件, 两处提示都只管发事件。
 *   监听方走的是**既有**的 selectTab() —— 它已经负责了 URL replaceState、redraw 和
 *   水平滚动定位 active tab (v0.6.32 那套防抖动的逻辑), 这里不重复实现一遍。
 *
 * 【两种版式的分工】
 *   panel   —— 课表日历的整块空态 (容器有 min-height:300px, 需要居中大块排版)
 *   compact —— 约课弹窗里替换 .ZS-form-input 的小占位 (表单行高受限, 不能撑成大块)
 *   两者的文字与按钮**逐字相同**, 只有外框尺寸不同。
 *
 * ⚠️ 视觉一律复用既有 class (.ZS-newversion-hint / .ZS-form-input / .ZS-btn),
 *   本轮**不新增一条 LESS** —— 布局微调用内联 style (与本仓库既有写法一致)。
 */

/** 页面侧监听的事件名。改这里必须同时改 SchedulerIndexPage 的监听处。 */
export const GO_SCHOOLS_EVENT = 'ziven-scheduler:go-schools';

// ─── 文案 (全仓库唯一一份) ───────────────────────────────────────────────

export const SCHEDULE_MISSING_TITLE = '该校区还没有配置课表';
export const SCHEDULE_MISSING_BODY = '课表决定这个校区每天开放哪几个时间段。到「校区」标签页, 在校区卡片上点「课表」按钮即可配置。';
export const SCHEDULE_MISSING_ACTION = '去「校区」配置课表';

/**
 * 加载态文案。
 * ⚠️ 「加载课表时间段中...」这句是改前 LessonEditorModal 里就有的**真实加载态**文案
 *   (不是死路), 本轮原样保留, 并让日历那边也用同一句 —— 三处加载态从此也不会分叉。
 */
export const SCHEDULE_LOADING_TEXT = '加载课表时间段中...';

// ─── v0.17.72 「问失败了」自己的文案 (第四态) ────────────────────────────
//
// 【为什么必须单列一套, 不能借用上面的「没配」文案】
//   事故现场 (辉哥 10/16): 学员点日历, 看到「校区没有课表」。**真相是后端 403 权限不足** ——
//   403 / 500 / 断网 / 超时 全被 `.catch(() => null)` 压成 null, 而 null 正是
//   「该校区没配课表」那个值。于是**后端说「你没权限」, 前端显示「你没配置」**。
//   照着「没配」去校区 tab 配一遍课表, 什么也不会变 —— 用户被指向了一个错的出口。
//
//   v0.17.61 已经把「还没问出结论」(loading) 与「问过了就是没有」(missing) 分开了, 但把
//   「问失败了」整个并进了 missing。三态不够, 是**四态**。
//
// ⚠️ 文案里必须写明「这不代表没配」: 只换个标题的话, 用户依然会跑去配置课表。
export const SCHEDULE_ERROR_TITLE = '课表加载失败';
export const SCHEDULE_ERROR_BODY = '没能读到该校区的时间段 —— 可能是网络问题, 也可能是权限不足。这不代表该校区没配课表, 请重试; 反复失败请联系管理员。';
export const SCHEDULE_ERROR_ACTION = '重新加载';

// ─── 出口 ────────────────────────────────────────────────────────────────

/**
 * 跳到「校区」tab (课表配置入口)。
 * @param {Function} [onBeforeNavigate] 跳转前的收尾钩子。约课弹窗要把**自己先关掉** ——
 *   否则 modal 浮层仍盖在上面, 用户以为没跳转 (Modal 是挂在 body 上的全局子树,
 *   切 tab 不会把它带走)。
 */
export function goConfigureSchedule(onBeforeNavigate) {
  if (typeof onBeforeNavigate === 'function') onBeforeNavigate();
  window.dispatchEvent(new CustomEvent(GO_SCHOOLS_EVENT));
}

// ─── 版式 ────────────────────────────────────────────────────────────────

/** 课表日历的空态大块。onBeforeNavigate 同上 (日历在页面内, 一般不用传)。 */
export function scheduleMissingPanel(onBeforeNavigate) {
  return m("div", {
    className: "ZS-sched-missing-panel",
    style: "min-height:300px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:36px 24px;box-sizing:border-box;"
  }, m("i", {
    className: "fas fa-calendar-days",
    style: "font-size:30px;color:var(--zs-text-2-8d96a3, #8d96a3);margin-bottom:12px;"
  }), m("div", {
    className: "ZS-newversion-hint",
    style: "margin-top:0;max-width:560px;text-align:center;padding:16px 20px;"
  }, m("div", {
    style: "font-size:15px;font-weight:600;margin-bottom:6px;"
  }, SCHEDULE_MISSING_TITLE), m("div", {
    style: "font-size:13px;line-height:1.6;"
  }, SCHEDULE_MISSING_BODY)), m("button", {
    type: "button",
    className: "ZS-btn",
    style: "margin-top:16px;",
    onclick: () => goConfigureSchedule(onBeforeNavigate)
  }, m("i", {
    className: "fas fa-location-dot"
  }), SCHEDULE_MISSING_ACTION));
}

/** 约课弹窗里替换 .ZS-form-input 的小占位。文案与按钮跟 panel 逐字一致。 */
export function scheduleMissingCompact(onBeforeNavigate) {
  return m("div", {
    className: "ZS-form-input"
  }, m("div", {
    style: "font-weight:600;"
  }, SCHEDULE_MISSING_TITLE), m("div", {
    style: "font-size:12px;opacity:0.75;margin-top:4px;line-height:1.5;"
  }, SCHEDULE_MISSING_BODY), m("button", {
    type: "button",
    className: "ZS-btn",
    style: "margin-top:10px;padding:6px 14px;font-size:12px;",
    onclick: () => goConfigureSchedule(onBeforeNavigate)
  }, m("i", {
    className: "fas fa-location-dot"
  }), SCHEDULE_MISSING_ACTION));
}

// ─── v0.17.72 「问失败了」版式 ───────────────────────────────────────────
//
// ⚠️ 这两个**刻意不复用**上面那套 DOM 与出口:
//   · 出口不同 —— 「没配」把人送去校区 tab 配置; 「问失败」只能重试。
//     失败时给「去配置课表」按钮, 就是在教用户做一件修不好这件事的操作。
//   · 视觉沿用同一批既有 class, 不新增 LESS (与本文件既有约束一致)。
//
// onRetry 必传: 没有重试的报错块只是个死胡同, 用户只能刷新整页。

/** 课表日历的失败态大块。与 scheduleMissingPanel 同款版式, 换文案 + 换出口。 */
export function scheduleErrorPanel(onRetry) {
  return m("div", {
    className: "ZS-sched-error-panel",
    style: "min-height:300px;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:36px 24px;box-sizing:border-box;"
  }, m("i", {
    class: "fas fa-triangle-exclamation",
    style: "font-size:30px;color:var(--zs-warning-c9821a, #c9821a);margin-bottom:12px;"
  }), m("div", {
    className: "ZS-newversion-hint",
    style: "margin-top:0;max-width:560px;text-align:center;padding:16px 20px;"
  }, m("div", {
    style: "font-size:15px;font-weight:600;margin-bottom:6px;"
  }, SCHEDULE_ERROR_TITLE), m("div", {
    style: "font-size:13px;line-height:1.6;"
  }, SCHEDULE_ERROR_BODY)), m("button", {
    type: "button",
    className: "ZS-btn",
    style: "margin-top:16px;",
    onclick: () => {
      if (typeof onRetry === 'function') onRetry();
    }
  }, m("i", {
    class: "fas fa-rotate-right"
  }), SCHEDULE_ERROR_ACTION));
}

/** 约课弹窗里的失败态小占位。文案与按钮跟 scheduleErrorPanel 逐字一致。 */
export function scheduleErrorCompact(onRetry) {
  return m("div", {
    className: "ZS-form-input"
  }, m("div", {
    style: "font-weight:600;"
  }, SCHEDULE_ERROR_TITLE), m("div", {
    style: "font-size:12px;opacity:0.75;margin-top:4px;line-height:1.5;"
  }, SCHEDULE_ERROR_BODY), m("button", {
    type: "button",
    className: "ZS-btn",
    style: "margin-top:10px;padding:6px 14px;font-size:12px;",
    onclick: () => {
      if (typeof onRetry === 'function') onRetry();
    }
  }, m("i", {
    class: "fas fa-rotate-right"
  }), SCHEDULE_ERROR_ACTION));
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/ScheduleMissingNotice', { GO_SCHOOLS_EVENT: GO_SCHOOLS_EVENT,SCHEDULE_MISSING_TITLE: SCHEDULE_MISSING_TITLE,SCHEDULE_MISSING_BODY: SCHEDULE_MISSING_BODY,SCHEDULE_MISSING_ACTION: SCHEDULE_MISSING_ACTION,SCHEDULE_LOADING_TEXT: SCHEDULE_LOADING_TEXT,SCHEDULE_ERROR_TITLE: SCHEDULE_ERROR_TITLE,SCHEDULE_ERROR_BODY: SCHEDULE_ERROR_BODY,SCHEDULE_ERROR_ACTION: SCHEDULE_ERROR_ACTION,goConfigureSchedule: goConfigureSchedule,scheduleMissingPanel: scheduleMissingPanel,scheduleMissingCompact: scheduleMissingCompact,scheduleErrorPanel: scheduleErrorPanel,scheduleErrorCompact: scheduleErrorCompact, });